import { isEmpty } from 'Helpers/objects';
import head from 'lodash/head';
import { getUrlParams, queryStringToParams, paramsToQueryString } from '../../helpers/url';
import { initialStateBase } from './newCollection';
import { reducer } from './newCollection';
import { getType } from 'Reducers/helpers/typeModifier';
import { PLATFORM } from 'Constants/device.APP_TARGET';

export const urls = {
    getExistingUser: id => `/api/users/${id}`,
    getUserData: '/api/users/me',
    getMeta: id => `/api/users/${id}/meta`,
    getFriendsInCommon: targetId => `/api/users/${targetId}/mutualfriends`,
    followUser: id => `/api/users/${id}/follow`,
    unFollowUser: id => `/api/users/${id}/unfollow`,
    userIsFollowing: (userId, targetUserId) => `/api/users/${userId}/following/${targetUserId}`,
    linkAccount: id => `/api/users/${id}/linkaccount`,
    unLinkAccount: id => `/api/users/${id}/unlink-account`,
    getItemBuyers: id => `/api/items/${id}/buyers`,
    findUser: emailPhone => `/api/users/find/${ emailPhone }`,
    findUserV2: '/api/users/find',
    forgotPassword: '/api/account/recovery',
    recoverPassword: token => `/api/account/recovery/${ token }`,
    postImage: '/api/v2/images',
    deleteImage: imageId => `/api/users/image/${imageId}`,
    getFollowing: userId => `/api/users/${userId}/following`,
    getFollowers: userId => `/api/users/${userId}/followers`,
    getUserImagesData: (userId, type) => `/api/users/${userId}/images?type=${type}`,
    getBulkUsers: '/api/users',
    logoutAll: '/api/auth/logout-all',
    getFaqData: `/api/cxe/widgets/cla-posting-learn-more-pwa-${PLATFORM}`
};

const initialState = Object.assign({}, initialStateBase, {
    metadataKey: 'users'
});

export const customActions = {

    operateCounter: (userId, counter, operator, param, sellerUserId) => {
        return {
            type: 'OPERATE_COUNTERS_USERS',
            data: {
                userId,
                counter,
                operator,
                param,
                sellerUserId
            }
        };
    }
};

const transformGetData = (state, url, response) => {
    const urlWithoutParams = url.split('?')[0];
    const urlParams = getUrlParams(urlWithoutParams);

    switch (urlWithoutParams) {
        case urls.getFriendsInCommon(urlParams[0]):
            response.data = response.data.users;
            break;
        case urls.userIsFollowing(urlParams[0], urlParams[1]):
            return {
                data: response
            };
        case urls.getItemBuyers(urlParams[0]):
        case urls.findUser(urlParams[0]):
            return {
                data: response.data
            };
    }

    return response;
};
const getLangParam = url => {
    const params = queryStringToParams(url.split('?')[1]);

    return params && params.lang ? params.lang : null;
};

const getURL = (url, lang) => {
    const newUrl = lang ? `${url}?${paramsToQueryString({ lang })}` : url;
    const type = getType('USERS', newUrl);

    return type;
};

const transformPostData = (state, url, response, params) => {
    const urlWithoutParams = url.split('?')[0];
    const lang = getLangParam(url);
    const urlParams = getUrlParams(urlWithoutParams);

    switch (urlWithoutParams) {
        case urls.followUser(urlParams[0]): {
            const userId = urlParams[0];
            const targetId = head(queryStringToParams(params).userIds);

            state.collections[getURL(urls.userIsFollowing(userId, targetId), lang)] = isEmpty(response.data);
            state.collections[getURL(urls.getMeta(targetId), lang)].counters.followers++;

            if (state.collections[getURL(urls.getFollowing(userId), lang)]) {
                state.collections[getURL(urls.getFollowing(userId), lang)].unshift(targetId);
            }

            break;
        }
        case urls.unFollowUser(urlParams[0]): {
            const userId = urlParams[0];
            const targetId = head(queryStringToParams(params).userIds);

            state.collections[getURL(urls.userIsFollowing(userId, targetId), lang)] = !isEmpty(response.data);

            if (state.collections[getURL(urls.getFollowing(userId), lang)]) {
                state.collections[getURL(urls.getFollowing(userId), lang)] = state.collections[getURL(urls.getFollowing(userId), lang)].filter(id => id !== targetId);
            }

            if (state.collections[getURL(urls.getMeta(targetId), lang)]) {
                state.collections[getURL(urls.getMeta(targetId), lang)].counters.followers--;
            }

            break;
        }
    }

    return response;
};

const transformCounters = (state, action) => {
    const param = action && action.data && action.data.param;
    const url = getURL(urls.getFollowers(action.data.sellerUserId), param && param.lang);

    if (state.collections[url]) {
        switch (action.data.operator) {
            case 'SUBTRACT':
                if (state.collections[url].indexOf(action.data.userId) > -1) {
                    state.collections[url].splice(state.collections[url].indexOf(action.data.userId), 1);
                }
                break;
            case 'SUM':
                if (!state.collections[url].includes(action.data.userId)) {
                    state.collections[url].push(action.data.userId);
                }
                break;
        }
    }

    return state;
};

const findNonExistingUsers = metadata => {
    if (!metadata && !metadata.non_existing) {
        return [];
    }

    const { non_existing } = metadata;

    if (non_existing && non_existing.length) {
        return non_existing.map(id => ({
            id: `${id}`,
            status: 'inactive'
        }));
    }

    return [];
};

const transformElementData = (state, { data, metadata }) => {
    let users = [];

    if (data.length > 0) {
        users = data.map(user => {
            if (user && user.id && state.elements && state.elements[user.id]) {
                return {
                    ...state.elements[user.id],
                    ...user
                };
            }

            return user;
        });
    }

    const nonExistingUsers = findNonExistingUsers(metadata);

    return { data: [...users, ...nonExistingUsers], metadata };
};

const transformElementMetadata = (state, metadata) => {
    if (metadata && metadata.users) {
        const mergedUsers = {};

        Object.keys(metadata.users).forEach(id => {
            if (metadata.users[id] && state.elements && state.elements[id]) {
                mergedUsers[id] = {
                    ...state.elements[id],
                    ...metadata.users[id]
                };
            }
            else {
                mergedUsers[id] = metadata.users[id];
            }
        });

        return { ...metadata, users: mergedUsers };
    }

    return metadata;
};

export default (state = initialState, action) => {
    switch (action.type) {
        case 'OPERATE_COUNTERS_USERS':
            return transformCounters(state, action);
        case 'RECEIVE_USERS':
            action.data = transformGetData(state, action.url, action.data);
            return reducer(state, action, 'USERS');
        case 'RESPONSE_POST_USERS':
            action.data = transformPostData(state, action.url, action.data, action.params);
            return reducer(state, action, 'USERS');
        case 'RESPONSE_POST_PASSWORD':
            return reducer(state, action, 'PASSWORD');
        case 'REQUEST_BULK_USERS':
        case 'ERRROR_BULK_USERS':
            return reducer(state, action, 'BULK_USERS');
        case 'RECEIVE_BULK_USERS':
            action.data = transformElementData(state, action.data);
            return reducer(state, action, 'BULK_USERS');
        default:
            if (action && action.data && action.data.metadata) {
                action.data.metadata = transformElementMetadata(state, action.data.metadata);
            }
            return reducer(state, action, 'USERS');
    }
};
